<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import gen from "@/functions/general.js"
import patientapi from "@/api/patientapi"
import { useCurrentEntryStore } from '@/stores/currentEntry'
import { useUiStore } from '@/stores/ui'
import { useI18n } from "vue-i18n";
import { FolderPatientApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
const { t } = useI18n();

const uiStore = useUiStore()
const currentEntryStore = useCurrentEntryStore()
const dropd = ref(null)

const folderPatientApi = new FolderPatientApi(medServerApiConfig);

const props = defineProps({
  isCurrentCs: {
    type: Boolean,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    required: true,
  },
  item1: {
    type: Object,
    required: true,
  },
  item2: {
    type: Object,
    required: true,
  },
  item3: {
    type: Object,
    required: false,
  },     
})

const focus = ref(null)

function medicAction(expr) {
  uiStore.setCclTextSelection("");
  currentEntryStore.setPoppedSapExpr(expr);
  currentEntryStore.setCurrSuggestionsForDashboard(null);
  uiStore.setDashBoardItem(expr);
}

async function patientDocAction(documentExpr) {
  currentEntryStore.setMiddleTab('docTemplate')
  openCclModal(false)  
  //Reload
  currentEntryStore.emptyCurrentDoc()
  const fullPatientDoc = await patientapi.getPatientDocument(documentExpr.patientDocumentId, currentEntryStore.currentEntryId)
  currentEntryStore.setCurrentDoc(fullPatientDoc)
}


const sortedSapExprArr = computed(() => {
  let ret = gen.deepCopy(props.item2.sapExprArr)
  return ret.sort((a, b) => b.order > a.order)
})

async function blurCcl() {
  const newText = conclusionText.value
  const conclusionGroup = props.item2
  const conclusionGroupId = conclusionGroup.conclusionGroupId
  const oldText = conclusionGroup.conclusionText
  if (newText !== oldText) {
    updateConclusionText(conclusionGroupId, newText)
  }
}

async function focusCcl() {
  currentEntryStore.setMiddleTab('cclSuggestions')
  currentEntryStore.allExpressions = await folderPatientApi.getAlertExpressions({
      patientId : currentEntryStore.currentPatientId});
  openCclModal()
}

async function inputCcl() {
  const newText = conclusionText.value
  debouncedEval(newText)
}

async function updateConclusionText(conclusionGroupId, updatedText) {
  await patientapi.updateConclusionText(conclusionGroupId, updatedText)
  await currentEntryStore.reloadCurrentEntry();
}

const debouncedEval = useDebounceFn((cclText) => {
  currentEntryStore.updateCclSuggestionsForCclText(cclText)
}, 500);

async function openCclModal(hasReload = true) {
  //Set item1, 2, 3
  uiStore.setCclTextSelection("")
  currentEntryStore.clearCurrExpr_Ccl()  
  // currentEntryStore.clearBodyParts()

  if (props.isCurrentCs && props.item2.groupOrder !== 0) {
    currentEntryStore.setCurrentCs(props.item1)
    currentEntryStore.setCurrentCcl(props.item2)
    currentEntryStore.setCurrentResult(props.item3)

    //Update suggestions
    if (hasReload) {
      currentEntryStore.updateCclSuggestionsForCclText(conclusionText.value)
      const currCclId = props.item2.conclusionGroupId.toString()
      if (props.item2.groupOrder != -1) {
        currentEntryStore.updateCclSuggestionsForResults(currCclId);
      }
    }
  }

}

function selFromInput(event) {
  const el = event.target
  const text = el.value.substring(el.selectionStart, el.selectionEnd).trim()
  const selectionText = (text.length  < 2) ? '' : text
  uiStore.setCclTextSelection(selectionText)
}

function focusToConclusionIfCurrent() {
  if (props.item2.conclusionGroupId != currentEntryStore.conclusionIdToFocus) return;
  focusToConclusion()
}

function focusToConclusion() {
  currentEntryStore.setMiddleTab('cclSuggestions')
  openCclModal()

  const cclGroupId = props.item2.conclusionGroupId
  const assessedConclusionElementId = 'conclusionTitle_' + cclGroupId
  currentEntryStore.setConclusionIdToFocus("")
  nextTick(() => {
    //Get Elements
    const assessedCclElement = document.getElementById(assessedConclusionElementId)
    if (!assessedCclElement) {console.log("Didn't find element with id: " + assessedConclusionElementId); return;}
    const inputElement = focus.value.innerRef
    //Scroll and focus
    if (!isElementVerticallyVisible(assessedCclElement)) {
      assessedCclElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    inputElement.focus()
  })
}

function isElementVerticallyVisible(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top >= 0 && rect.bottom <= windowHeight;
}

function focusToThisConclusion() {
  if (!props.isCurrentCs) return;
  if (props.item2.groupOrder === 0) return;
  const inputElement = focus.value.innerRef
  inputElement.focus()
}

const conclusionText = ref("")
const emits = defineEmits(['displayTemplateDocArr', 'displaySapExprArr']);

watch(
  () => currentEntryStore.conclusionIdToFocus,
  () => {focusToConclusionIfCurrent()},
  { immediate: true }
)

watch(
    () => props.item2,
  () => {
    if (props.item2) {
      if (props.item2.templateDocArr && props.item2.templateDocArr.length > 0) {
        emits('displayTemplateDocArr', props.item2.templateDocArr);
      }
      if (props.item2.sapExprArr && props.item2.sapExprArr.length > 0) {
        emits('displaySapExprArr', props.item2.sapExprArr);
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  conclusionText.value = props.item2.conclusionText
})


</script>

<template>
  <div class="pleft-08 row-start h-full w-full" :style="(isCurrentCs && item2.groupOrder !== 0) ? 'cursor: text' : ''" @click="focusToThisConclusion()">
    <div class="w-full col gap-02 mtop-24px" v-if="item2.groupOrder !== 0">
      <template v-if="isCurrentCs">
        <!-- Ccl-Text -->
        <div class="input-container" :class="(props.item2.conclusionGroupId === currentEntryStore.currentCclId && currentEntryStore.middleTab === 'cclSuggestions') ? 'input-container-active' : ''">
          <inputAreaAuto2
              v-if="!uiStore.cclDocumentFilter"
              class="w-full"
              tabindex="-1"
              :placeholder="t('patient.conclusionGroup.placeholder')"
              styles="pleft-04 pad-0"
              :id="'conclusion_' + item2.conclusionGroupId"
              ref="focus"
              :mic="false"
              v-model="conclusionText"
              @click.stop
              @blur="blurCcl()"
              @focus="focusCcl()"
              @input="inputCcl()"
              @selectionchange="selFromInput"
          />
        <div :class="[{ 'with-border': isCurrentCs && item2.groupOrder !== 0 && !uiStore.cclDocumentFilter}]">


            <!-- Bio & Medication ROW -->
            <div class="row flexwrap marg-s">
              <!-- Bio -->
              <template v-if="props.item2.bioPrescrArr.length > 0">
                <dropdownBox v-model="dropd" openOn='Bio' class="params-row mleft-02 mtop-2px">
                  <buttonGroup styles="pad-s mright-01" :maxWidth="true">
                    <svg-item icon="beaker" class="item-center" :size="'12px'"/>
                    <text-item :txt="props.item2.bioPrescrArr.length.toString()" class="mleft-01"/>
                  </buttonGroup>
                  <template #dropd>
                    <div class="bg-modal w-maxC">
                      <dbGroup>
                        <template v-for="(item, index) in props.item2.bioPrescrArr" :key="index">
                          <text-item :txt="item.originalText" class="bio-text-max-w"/>
                        </template>
                      </dbGroup>
                    </div>
                  </template>
                </dropdownBox>
              </template>
              <!-- Medication -->
              <template v-if="sortedSapExprArr.length">
                <div v-for="(item, index) in sortedSapExprArr" :key="index" class="params-row mleft-02 mbottom-02">
                  <buttonGroup styles="pad-s mbottom-02 mright-01" :maxWidth="true" @click.stop="medicAction(item)">
                    <div class="w-maxC">
                      <svg-item :icon="item.icon" class="item-center square-12"/>
                    </div>
                    <text-item :txt="item.text" class="mleft-01"/>
                  </buttonGroup>
                </div>
              </template>
            </div>
              
            <!-- Document ROW -->
            <div class="row flexwrap marg-s">
              <div v-for="(item, index) in props.item2.templateDocArr" :key="index">
                <div v-if="isCurrentCs || ['Complete', 'Scheduled', 'Exported'].includes(item.documentExpression.templateStatus)" class="params-row mleft-02 mbottom-02" >
                  <buttonGroup styles="pad-s mbottom-02 mright-01" :maxWidth="true" @click.stop="patientDocAction(item.documentExpression)">
                    <div class="w-maxC">
                      <svg-item :icon="item.icon" class="item-center square-12"/>
                    </div>                    
                    <text-item :txt=item.text class="mleft-01"/>
                  </buttonGroup>
                </div>
              </div>
            </div>

          </div>
          <div class="mtop-02"></div>
        </div>
        <div class="mtop-02"></div>
      </template>

      <!-- Text of previous Cs -->
      <template v-else>
        <template v-if="(sortedSapExprArr.length || props.item2.bioPrescrArr.length || props.item2.templateDocArr.length || item2.conclusionText) && item2.groupOrder !== 0">
          <div class="input-container">

            <!-- Ccl Text -->
            <text-item v-if="!uiStore.cclDocumentFilter && item2.conclusionText" :txt="item2.conclusionText" styles="font-m" style="user-select: auto; cursor: default;" class="mleft-03 linebreak" :class="[{ 'pbottom-08': (sortedSapExprArr.length|| props.item2.bioPrescrArr.length) && item2.conclusionText}]"/>
            <div :class="[{ 'with-border': (sortedSapExprArr.length || props.item2.bioPrescrArr.length) && item2.conclusionText &&  !uiStore.cclDocumentFilter, 'no-pad-top' : !item2.conclusionText}]" >

              <!-- Bio & Medication ROW-->
              <div v-if="(props.item2.bioPrescrArr.length && props.isExpanded) || sortedSapExprArr.length" class="row flexwrap marg-s">
                <!-- Bio -->
                <template v-if="props.item2.bioPrescrArr.length && props.isExpanded">
                  <dropdownBox v-model="dropd" openOn='Bio' class="params-row mleft-02 mtop-2px">
                    <buttonGroup styles="pad-s mright-01" :maxWidth="true">
                      <svg-item icon="beaker" class="item-center" :size="'12px'"/>
                      <text-item :txt="props.item2.bioPrescrArr.length.toString()" class="mleft-01"/>
                    </buttonGroup>
                    <template #dropd>
                      <div class="bg-modal w-maxC">
                        <dbGroup>
                          <template v-for="(item, index) in props.item2.bioPrescrArr" :key="index">
                            <text-item :txt="item.originalText" class="bio-text-max-w"/>
                          </template>
                        </dbGroup>
                      </div>
                    </template>
                  </dropdownBox>
                </template>
                <!-- Medication -->
                <template v-if="sortedSapExprArr.length">
                  <div v-for="(item, index) in sortedSapExprArr" :key="index" class="params-row mleft-02 mbottom-02">
                    <buttonGroup styles="pad-s mbottom-02 mright-01" :maxWidth="true" @click.stop="medicAction(item)">
                      <svg-item :icon="item.icon" class="item-center"/>
                      <text-item :txt="item.text" class="mleft-01"/>
                    </buttonGroup>
                  </div>
                </template>
              </div>

              <!-- Document ROW -->
              <div v-if="props.item2.templateDocArr.length" class="row flexwrap marg-s">
                <template v-for="(item, index) in props.item2.templateDocArr" :key="index">
                  <div v-if="['Complete', 'Scheduled', 'Exported'].includes(item.documentExpression.templateStatus)" class="params-row mleft-02 mbottom-02" >
                    <buttonGroup styles="pad-s mbottom-02 mright-01" :maxWidth="true" @click.stop="patientDocAction(item.documentExpression)">
                      <svg-item :icon="item.icon" class="item-center"/>
                      <text-item :txt=item.text class="mleft-01"/>
                    </buttonGroup>
                  </div>
                </template>
              </div>

            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.no-pad-top {
  padding-top: 0px!important;
}

.input-container {
  padding: 8px;
  background: var(--blue-04);
  border-radius: 12px;
  width: 100%;
  transition: all 0.3s ease;  
  border-color: var(--blue-04);
  border-style: solid;
  border-width: 1px;  
}

.input-container-active {
  background: var(--primary-100);
  border-color: var(--primary-200);
}

.with-border {
  border-top: 1px solid black;
}

p {
  width: 100%;
}

.params-row {
  display: flex;
  flex-wrap: wrap;
  padding-top: 8px;
}

.params-row > div {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 6px;
  gap: 4px;
  background: rgba(30, 30, 30, 0.08);
  border: 1px solid rgba(30, 30, 30, 0.04);
  backdrop-filter: blur(7.5px);
  border-radius: 999px;
}

.params-row > div span {
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: #1E1E1E;
}
</style>